import React, { useState, useEffect } from 'react';

function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always true, can't be disabled
    analytics: false,
    functional: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Show banner after a short delay
      setTimeout(() => setShowBanner(true), 1000);
    } else {
      // Load saved preferences
      try {
        const saved = JSON.parse(consent);
        setPreferences(saved);
      } catch (e) {
        console.error('Error loading cookie preferences:', e);
      }
    }
  }, []);

  const acceptAll = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      functional: true
    };
    setPreferences(allAccepted);
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setShowBanner(false);
    setShowSettings(false);
  };

  const acceptSelected = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(preferences));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setShowBanner(false);
    setShowSettings(false);
  };

  const rejectAll = () => {
    const onlyEssential = {
      essential: true,
      analytics: false,
      functional: false
    };
    setPreferences(onlyEssential);
    localStorage.setItem('cookieConsent', JSON.stringify(onlyEssential));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setShowBanner(false);
    setShowSettings(false);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Overlay */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 9998,
        backdropFilter: 'blur(2px)'
      }} />

      {/* Cookie Banner */}
      <div style={{
        position: 'fixed',
        bottom: showSettings ? '50%' : '20px',
        left: '50%',
        transform: showSettings ? 'translate(-50%, 50%)' : 'translateX(-50%)',
        maxWidth: showSettings ? '600px' : '800px',
        width: '90%',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        zIndex: 9999,
        padding: '30px',
        transition: 'all 0.3s ease',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '20px' }}>
          <span style={{ fontSize: '32px', marginRight: '15px' }}>🍪</span>
          <div>
            <h3 style={{
              margin: '0 0 10px 0',
              fontSize: '22px',
              color: '#212529',
              fontWeight: '700'
            }}>
              {showSettings ? 'Cookie Settings' : 'We Value Your Privacy'}
            </h3>
            <p style={{
              margin: 0,
              fontSize: '15px',
              color: '#495057',
              lineHeight: '1.6'
            }}>
              {showSettings 
                ? 'Choose which cookies you want to accept. Essential cookies are required for the app to function.'
                : 'We use cookies to enhance your experience, analyze site usage, and provide personalized content. By clicking "Accept All", you consent to our use of cookies.'
              }
            </p>
          </div>
        </div>

        {showSettings && (
          <div style={{ marginBottom: '20px' }}>
            {/* Essential Cookies */}
            <div style={{
              padding: '15px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              marginBottom: '12px',
              border: '1px solid #dee2e6'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <label style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#212529',
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'not-allowed'
                }}>
                  <input
                    type="checkbox"
                    checked={preferences.essential}
                    disabled
                    style={{ marginRight: '10px', cursor: 'not-allowed' }}
                  />
                  Essential Cookies
                </label>
                <span style={{
                  backgroundColor: '#28a745',
                  color: 'white',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: '600'
                }}>
                  Required
                </span>
              </div>
              <p style={{
                margin: '0 0 0 30px',
                fontSize: '13px',
                color: '#6c757d',
                lineHeight: '1.5'
              }}>
                Necessary for the website to function. These cookies enable basic features like authentication, security, and session management.
              </p>
            </div>

            {/* Analytics Cookies */}
            <div style={{
              padding: '15px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              marginBottom: '12px',
              border: '1px solid #dee2e6'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <label style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#212529',
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                    style={{ marginRight: '10px', cursor: 'pointer' }}
                  />
                  Analytics Cookies
                </label>
                <span style={{
                  backgroundColor: '#17a2b8',
                  color: 'white',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: '600'
                }}>
                  Optional
                </span>
              </div>
              <p style={{
                margin: '0 0 0 30px',
                fontSize: '13px',
                color: '#6c757d',
                lineHeight: '1.5'
              }}>
                Help us understand how visitors interact with our website by collecting and reporting information anonymously. Used to improve user experience.
              </p>
            </div>

            {/* Functional Cookies */}
            <div style={{
              padding: '15px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              marginBottom: '12px',
              border: '1px solid #dee2e6'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <label style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#212529',
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={preferences.functional}
                    onChange={(e) => setPreferences({ ...preferences, functional: e.target.checked })}
                    style={{ marginRight: '10px', cursor: 'pointer' }}
                  />
                  Functional Cookies
                </label>
                <span style={{
                  backgroundColor: '#17a2b8',
                  color: 'white',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: '600'
                }}>
                  Optional
                </span>
              </div>
              <p style={{
                margin: '0 0 0 30px',
                fontSize: '13px',
                color: '#6c757d',
                lineHeight: '1.5'
              }}>
                Enable enhanced functionality and personalization, such as saved searches, preferences, and customized content.
              </p>
            </div>
          </div>
        )}

        <div style={{
          display: 'flex',
          gap: '10px',
          marginTop: '20px',
          flexWrap: 'wrap'
        }}>
          {showSettings ? (
            <>
              <button
                onClick={acceptSelected}
                style={{
                  flex: '1',
                  minWidth: '140px',
                  padding: '12px 24px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#0056b3'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#007bff'}
              >
                Save Preferences
              </button>
              <button
                onClick={() => setShowSettings(false)}
                style={{
                  flex: '1',
                  minWidth: '140px',
                  padding: '12px 24px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#5a6268'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#6c757d'}
              >
                Back
              </button>
            </>
          ) : (
            <>
              <button
                onClick={acceptAll}
                style={{
                  flex: '1',
                  minWidth: '140px',
                  padding: '12px 24px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#218838'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#28a745'}
              >
                Accept All
              </button>
              <button
                onClick={() => setShowSettings(true)}
                style={{
                  flex: '1',
                  minWidth: '140px',
                  padding: '12px 24px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#0056b3'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#007bff'}
              >
                Customize
              </button>
              <button
                onClick={rejectAll}
                style={{
                  flex: '1',
                  minWidth: '140px',
                  padding: '12px 24px',
                  backgroundColor: 'white',
                  color: '#495057',
                  border: '2px solid #dee2e6',
                  borderRadius: '6px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                  e.currentTarget.style.borderColor = '#adb5bd';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'white';
                  e.currentTarget.style.borderColor = '#dee2e6';
                }}
              >
                Reject All
              </button>
            </>
          )}
        </div>

        <p style={{
          marginTop: '15px',
          marginBottom: 0,
          fontSize: '13px',
          color: '#6c757d',
          textAlign: 'center'
        }}>
          Read our <a href="/gdpr" style={{ color: '#007bff', textDecoration: 'none' }}>GDPR Policy</a> and{' '}
          <a href="/privacy" style={{ color: '#007bff', textDecoration: 'none' }}>Privacy Policy</a> for more information.
        </p>
      </div>
    </>
  );
}

export default CookieConsent;
